import { useState, useEffect } from "react";
import { BarChart3, Clock, Users, CheckCircle, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HomepagePollSection = () => {
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    fetchPoll();
  }, []);

  const fetchPoll = async () => {
    try {
      const res = await fetch(`${API}/homepage-poll`);
      if (res.ok) {
        const data = await res.json();
        setPoll(data);
      }
    } catch (err) {
      console.error("Error fetching poll:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionId) => {
    if (voting || poll?.has_voted || poll?.is_expired) return;

    setVoting(true);
    setSelectedOption(optionId);

    try {
      const res = await fetch(`${API}/homepage-poll/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ option_id: optionId }),
      });

      if (res.ok) {
        const updatedPoll = await res.json();
        setPoll(updatedPoll);
      } else {
        const err = await res.json();
        alert(err.detail || "Error voting");
        setSelectedOption(null);
      }
    } catch (err) {
      console.error("Error voting:", err);
      setSelectedOption(null);
    } finally {
      setVoting(false);
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;

    if (diff <= 0) return "Ended";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  };

  if (loading) {
    return null; // Don't show loading state, just hide section
  }

  if (!poll) {
    return null; // No active poll
  }

  const showResults = poll.show_results || poll.has_voted || poll.is_expired;
  const canVote = !poll.has_voted && !poll.is_expired;

  return (
    <section className="py-12 bg-zinc-900" data-testid="homepage-poll-section">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white uppercase tracking-wider">
                Fan Poll
              </h2>
              {!poll.is_expired && (
                <div className="flex items-center gap-1 text-xs text-zinc-400">
                  <Clock className="w-3 h-3" />
                  {formatTimeRemaining(poll.expires_at)}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Users className="w-4 h-4" />
            <span>{poll.total_votes.toLocaleString()} votes</span>
          </div>
        </div>

        {/* Poll Card */}
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          {/* Question */}
          <h3 className="text-2xl font-bold text-white mb-2">{poll.title}</h3>
          {poll.description && (
            <p className="text-zinc-400 mb-6">{poll.description}</p>
          )}

          {/* Expired Badge */}
          {poll.is_expired && (
            <div className="mb-4 px-3 py-2 bg-zinc-700 rounded-lg inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-zinc-400" />
              <span className="text-zinc-300 text-sm font-medium">
                Poll ended - Final results
              </span>
            </div>
          )}

          {/* Voted Badge */}
          {poll.has_voted && !poll.is_expired && (
            <div className="mb-4 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg inline-flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-green-400 text-sm font-medium">
                Thanks for voting!
              </span>
            </div>
          )}

          {/* Options */}
          <div className="space-y-3">
            {poll.options.map((option) => {
              const isSelected = selectedOption === option.id;
              const percentage = option.percentage || 0;

              return (
                <button
                  key={option.id}
                  onClick={() => canVote && handleVote(option.id)}
                  disabled={!canVote || voting}
                  className={`w-full relative overflow-hidden rounded-lg transition-all ${
                    canVote
                      ? "hover:ring-2 hover:ring-orange-500 cursor-pointer"
                      : "cursor-default"
                  } ${isSelected ? "ring-2 ring-orange-500" : ""}`}
                  data-testid={`poll-option-${option.id}`}
                >
                  {/* Background bar for results */}
                  {showResults && (
                    <div
                      className="absolute inset-0 bg-orange-500/20 transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  )}

                  {/* Content */}
                  <div
                    className={`relative flex items-center justify-between px-4 py-4 ${
                      showResults
                        ? "bg-zinc-700/50"
                        : "bg-zinc-700 hover:bg-zinc-600"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Vote indicator */}
                      {canVote && (
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected
                              ? "border-orange-500 bg-orange-500"
                              : "border-zinc-500"
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                      )}

                      {/* Check for voted option */}
                      {showResults && poll.has_voted && (
                        <div className="w-5 h-5 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-orange-500" />
                        </div>
                      )}

                      <span className="text-white font-medium">{option.text}</span>
                    </div>

                    {/* Results */}
                    {showResults && (
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-400 text-sm">
                          {option.votes?.toLocaleString()} votes
                        </span>
                        <span className="text-orange-500 font-bold text-lg min-w-[60px] text-right">
                          {percentage}%
                        </span>
                      </div>
                    )}

                    {/* Arrow for votable options */}
                    {canVote && !showResults && (
                      <ChevronRight className="w-5 h-5 text-zinc-500" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Loading indicator */}
          {voting && (
            <div className="mt-4 text-center text-zinc-400 text-sm">
              Submitting your vote...
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomepagePollSection;
